/**
 * Provides helpers for local URL generation
 */
export default class Navigation {
  /**
   * Path helpers
   */

  static selfUserProfilePath() {
    return `/p/me`;
  }

  static userProfilePath(userIdOrAlias) {
    return `/p/${userIdOrAlias}`;
  }

  static orgCreatePath(user) {
    if ( user.alias ) {
      return `/p/${user.alias}/org/create`;
    } else {
      return `/p/${user.id}/org/create`;
    }
  }

  static orgPath(org) {
    return `/${org.alias}`;
  }

  static userSettingsProfilePath(userId) {
    return `/p/${userId}/settings/profile`;
  }

  static userSettingsCredentialsPath(userId) {
    return `/p/${userId}/settings/credentials`;
  }

  static userSettingsApiKeysPath(userId) {
    return `/p/${userId}/settings/api-keys`;
  }

  static userSettingsAccountsPath(userId) {
    return `/p/${userId}/settings/accounts`;
  }

  static userSettingsLimitsPath(userId) {
    return `/p/${userId}/settings/limits`;
  }

  static userSettingsPreferencesPath(userId) {
    return `/p/${userId}/settings/preferences`;
  }

  static orgSettingsProfilePath(orgId) {
    return `/${orgId}/settings/profile`;
  }

  static orgSettingsAccessPath(orgId) {
    return `/${orgId}/settings/access`;
  }

  static orgSettingsApiKeysPath(orgId) {
    return `/${orgId}/settings/api-keys`;
  }

  static orgSettingsAccountsPath(orgId) {
    return `/${orgId}/settings/accounts`;
  }

  static orgSettingsCredentialsPath(orgId) {
    return `/${orgId}/settings/credentials`;
  }

  static orgSettingsLimitsPath(orgId) {
    return `/${orgId}/settings/limits`;
  }

  static orgSettingsPreferencesPath(orgId) {
    return `/${orgId}/settings/preferences`;
  }

  static orgSettingsAdminPath(orgId) {
    return `/${orgId}/settings/admin`;
  }

  static blobCreatePath(org) {
    return `/${org.alias}/blob/create`;
  }

  static blobPath(org, blob) {
    return `/${org.alias}/${blob.alias}`;
  }

  /**
   * A blob settings section, addressed by the ids ALREADY IN THE URL rather than by the resolved
   * aliases.
   *
   * This replaced seven near-identical helpers that each built `/${org.alias}/${blob.alias}/...`.
   * They were a link from the blob page to ITSELF, and they rewrote both params from whatever form
   * the user arrived with to the alias form — so a user who opened `/orgId/blobId/settings/profile`
   * and clicked "Access" landed on `/orgAlias/blobAlias/settings/access`: the same blob, a different
   * identity. That was invisible while the page never remounted on a param change. It stopped being
   * invisible when RouteWrapper began keying on orgId/blobId, where it would tear down the whole
   * blob shell on a sub-tab click and discard unsaved workflow and playground edits.
   *
   * Callers pass `match.params`, which is also what Blob's own tab strip already builds from.
   */
  static blobSettingsPath(orgId, blobId, section) {
    return `/${orgId}/${blobId}/settings/${section}`;
  }

  /**
   * Workflows
   */

  static workflowPath(orgId, blobId, section, sectionId, subSectionId) {
    if ( subSectionId ) {
      return `/${orgId}/${blobId}/${section}/${sectionId}/${subSectionId}`;
    } else if ( sectionId ) {
      return `/${orgId}/${blobId}/${section}/${sectionId}`;
    } else if ( section ) {
      return `/${orgId}/${blobId}/${section}`;
    }
  }

  /**
   * Schedulers
   */

  static schedulerPath(orgId, blobId, section, sectionId) {
    if ( sectionId ) {
      return `/${orgId}/${blobId}/${section}/${sectionId}`;
    } else if ( section ) {
      return `/${orgId}/${blobId}/${section}`;
    }
  }

  /**
   * Navigation helpers
   */

  /**
   * url contains fully-qualified path
   */
  static assign(url) {
    window.location.assign(url);
  }

  /**
   * Reloads the current route. Identity changes go through a full document load so no mounted
   * component keeps data fetched as somebody else.
   */
  static reload() {
    window.location.reload();
  }

  /**
   * path contains absolute URI path
   */
  static push(history, path) {
    history.push(path);
  }

  /**
   * Builds the react-router-v5-shaped `history` prop that page components receive, on top of
   * react-router v6's navigate(). Lives here (rather than inline in the route wrapper) because it is
   * the contract every page component navigates through, and a missing method on it fails only at
   * runtime, in whichever page happens to call it — `replace` was added after `syncDefinitionPath`
   * shipped calling a method the shim did not have.
   *
   *   push    — a new location the user can Back out of.
   *   replace — a CORRECTION to the current location (e.g. naming the definition that a create /
   *             rename / delete actually left selected), which should leave no Back-button step.
   */
  static historyShim(navigate) {
    return {
      push: path => navigate(path),
      replace: path => navigate(path, { replace: true })
    };
  }
}
